import { breakLineTag, whatsAppBreakLineTag, textAppBreakLineTag } from './MessageBreakLines';
import systemConfigService from '../services/SystemConfigService';

/**
 * MessagesTemplate
 *
 * Provides message templates for the application.
 * Templates are fetched from company configuration if available,
 * otherwise fallback to system-wide default templates.
 */
const MessagesTemplate = {
    /**
     * Convert message to WhatsApp format
     * @param {string} message - Message to convert
     * @returns {string} Converted message
     */
    ConvertToWhatsAppText: (message) => {
        if (!message) return '';
        
        let rtn = message;
        
        // Normalize different line break types to \n first
        // Handle Windows (\r\n), Mac (\r), and Unix (\n) line breaks
        rtn = rtn.replace(/\r\n/g, '\n'); // Windows
        rtn = rtn.replace(/\r/g, '\n');   // Mac
        
        // Convert double line breaks (paragraph breaks) to double WhatsApp breaks
        rtn = rtn.replace(/\n\n+/g, whatsAppBreakLineTag + whatsAppBreakLineTag);
        
        // Convert single line breaks to WhatsApp breaks
        rtn = rtn.replace(/\n/g, whatsAppBreakLineTag);
        
        // Convert existing break line tags to WhatsApp format
        rtn = rtn.replaceAll(breakLineTag, whatsAppBreakLineTag);
        
        // Convert any remaining text app break line tags
        rtn = rtn.replaceAll(textAppBreakLineTag, whatsAppBreakLineTag);
        
        return rtn;
    },

    /**
     * Convert message to clipboard text format
     * @param {string} message - Message to convert
     * @returns {string} Converted message
     */
    ConvertToClipboardText: (message) => {
        if (!message) return '';
        
        let rtn = message;
        
        // Normalize different line break types to \n first
        // Handle Windows (\r\n), Mac (\r), and Unix (\n) line breaks
        rtn = rtn.replace(/\r\n/g, '\n'); // Windows
        rtn = rtn.replace(/\r/g, '\n');   // Mac
        
        // Convert existing break line tags to text format
        rtn = rtn.replaceAll(breakLineTag, textAppBreakLineTag);
        
        // Convert WhatsApp break line tags to text format
        rtn = rtn.replaceAll(whatsAppBreakLineTag, textAppBreakLineTag);
        
        return rtn;
    },

    /**
     * Get template by key
     * @param {string} key - Template key
     * @param {Object} companyConfig - Company configuration
     * @returns {string} Template content
     */
    getTemplate: (key, companyConfig) => {
        // First try to get template from company config
        if (companyConfig && companyConfig.MessageTemplates) {
            const template = companyConfig.MessageTemplates.find(t => t.TemplateKey == key);
            
            if (template) {
                return template.TemplateContent;
            }
        }
        
        // If not found in company config, get from system config
        const systemTemplate = systemConfigService.getDefaultMessageTemplate(key);
        if (systemTemplate) {
            return systemTemplate;
        }
        
        // If not found in system config, use hardcoded fallback
        return '';
    },

    /**
     * Get all templates
     * @param {Object} companyConfig - Company configuration
     * @returns {Object} Object containing all templates
     */
    getTemplates: (companyConfig) => {
        const templates = {
            AppointmentCreatedFullMessage: MessagesTemplate.getTemplate('AppointmentCreatedFullMessage', companyConfig),
            AppointmentCreatedShortMessage: MessagesTemplate.getTemplate('AppointmentCreatedShortMessage', companyConfig),
            AppointmentReservationMessage: MessagesTemplate.getTemplate('AppointmentReservationMessage', companyConfig),
            AppointmentReservationReminder: MessagesTemplate.getTemplate('AppointmentReservationReminder', companyConfig),
            AppointmentReservationReleased: MessagesTemplate.getTemplate('AppointmentReservationReleased', companyConfig),
            AppointmentAskForConfirmation: [
                MessagesTemplate.getTemplate('AppointmentAskForConfirmation1', companyConfig),
                MessagesTemplate.getTemplate('AppointmentAskForConfirmation2', companyConfig),
                MessagesTemplate.getTemplate('AppointmentAskForConfirmation3', companyConfig)
            ],
            Appointment7DaysReminder: MessagesTemplate.getTemplate('Appointment7DaysReminder', companyConfig),
            AppointmentCancellationDueLackOfReturn: MessagesTemplate.getTemplate('AppointmentCancellationDueLackOfReturn', companyConfig),
            SuggestDate: MessagesTemplate.getTemplate('SuggestDate', companyConfig),
            HappyBirthday: MessagesTemplate.getTemplate('HappyBirthday', companyConfig),
            SuggestDateFromWaitingList: MessagesTemplate.getTemplate('SuggestDateFromWaitingList', companyConfig),
            AppointmentRescheduledConfirmedMessage: MessagesTemplate.getTemplate('AppointmentRescheduledConfirmedMessage', companyConfig),
            AppointmentCanceledMessage: MessagesTemplate.getTemplate('AppointmentCanceledMessage', companyConfig),
            CanceledAppointmentFollowUp: MessagesTemplate.getTemplate('CanceledAppointmentFollowUp', companyConfig),
            FirstAppointmentFollowup: MessagesTemplate.getTemplate('FirstAppointmentFollowup', companyConfig),
            NPSFeedbackRequest: MessagesTemplate.getTemplate('NPSFeedbackRequest', companyConfig),
            GoogleReviewRequest: MessagesTemplate.getTemplate('GoogleReviewRequest', companyConfig),
            AppointmentAssessmentTemplate: MessagesTemplate.getTemplate('AppointmentAssessmentTemplate', companyConfig),
            AppointmentEvolutionTemplate: MessagesTemplate.getTemplate('AppointmentEvolutionTemplate', companyConfig)
        };
        
        return templates;
    }
};

export default MessagesTemplate;